/**
 * Guest Mode limits (Option B: daily allowance).
 *
 * Guests can practice without an account, but are capped to:
 *  - a per-session limit (questions in any single session), and
 *  - a per-day allowance (total questions per calendar day, local time),
 *    which resets at local midnight.
 *
 * The daily count is stored under a date-keyed record in localStorage so it
 * naturally resets each day without a scheduler. This replaces the previous
 * permanent lifetime cap.
 */

import { storage } from './storage';

// Per-session cap for guests: the most a guest's Max Questions choice can be (guestSessionCap).
export const GUEST_PER_SESSION_LIMIT = 10;

// Per-day total cap for guests; resets at local midnight.
export const GUEST_DAILY_LIMIT = 30;

const GUEST_DAILY_KEY = 'kma_guest_daily';

interface GuestDailyRecord {
  /** Local calendar day this count applies to, formatted YYYY-MM-DD. */
  date: string;
  /** Questions answered by the guest on that day. */
  count: number;
}

/** Local calendar day as YYYY-MM-DD (not UTC, so reset matches the user's day). */
export function localDateKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * A guest session's question cap: the guest's Max Questions choice, but never more than
 * GUEST_PER_SESSION_LIMIT. Anything that isn't a positive whole number falls back to the limit.
 */
export function guestSessionCap(choice?: number): number {
  if (choice === undefined || !Number.isInteger(choice) || choice <= 0) {
    return GUEST_PER_SESSION_LIMIT;
  }
  return Math.min(choice, GUEST_PER_SESSION_LIMIT);
}

/** Read today's guest question count (0 if none or a stale prior day), never above the limit. */
export function getGuestDailyCount(now: Date = new Date()): number {
  const record = storage.get<GuestDailyRecord>(GUEST_DAILY_KEY);
  if (!record || record.date !== localDateKey(now)) return 0;
  // Sanitize: localStorage can be corrupted (e.g. count as a string/NaN), so
  // coerce to a non-negative integer and fall back to 0 when invalid. Older builds could
  // store more than the limit, so read those as the limit.
  const count = Number(record.count);
  if (!Number.isFinite(count)) return 0;
  return Math.min(GUEST_DAILY_LIMIT, Math.max(0, Math.floor(count)));
}

/** Persist an incremented guest daily count for today, capped at the limit; returns it. */
export function incrementGuestDailyCount(now: Date = new Date()): number {
  const next = Math.min(GUEST_DAILY_LIMIT, getGuestDailyCount(now) + 1);
  storage.set(GUEST_DAILY_KEY, { date: localDateKey(now), count: next });
  return next;
}

/** Questions remaining in today's allowance (never negative). */
export function getGuestDailyRemaining(now: Date = new Date()): number {
  return Math.max(0, GUEST_DAILY_LIMIT - getGuestDailyCount(now));
}

/** Whether the guest still has questions left in today's allowance. */
export function guestHasDailyQuestionsLeft(now: Date = new Date()): boolean {
  return getGuestDailyRemaining(now) > 0;
}
